import { ChallengeLocation } from '../types';

const pad = (n: number) => String(n).padStart(2, '0');
const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** "Sábado, 31 de outubro de 2026" */
export function fmtDateLong(iso: string): string {
  const txt = new Intl.DateTimeFormat('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(iso));
  return capitalize(txt).replace(/^([^,]+) /, '$1, ');
}

/** { day: '31', month: 'OUT' } */
export function fmtDayMonth(iso: string): { day: string; month: string } {
  const d = new Date(iso);
  const month = new Intl.DateTimeFormat('pt-BR', { month: 'short' })
    .format(d)
    .replace('.', '')
    .toUpperCase();
  return { day: pad(d.getDate()), month };
}

/** "8h" or "8h30" */
export function fmtHour(iso: string): string {
  const d = new Date(iso);
  const m = d.getMinutes();
  return `${d.getHours()}h${m ? pad(m) : ''}`;
}

/** "08:15" */
export function fmtClock(iso: string | null): string {
  if (!iso) return '--:--';
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** "8h às 18h" (a whole-day window shows "Dia todo") */
export function fmtTimeRange(startIso: string, endIso: string): string {
  const minutes =
    (new Date(endIso).getTime() - new Date(startIso).getTime()) / 60000;
  if (minutes >= 24 * 60) return 'Dia todo';
  return `${fmtHour(startIso)} às ${fmtHour(endIso)}`;
}

/** 45 -> "45 min", 120 -> "2h", 72 -> "1h12" */
export function fmtDuration(totalMinutes: number | null): string {
  const min = Math.max(0, Math.round(totalMinutes ?? 0));
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h}h${pad(m)}` : `${h}h`;
}

/** Seconds -> "01:12:05" */
export function fmtStopwatch(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
}

/** "Hoje", "Amanhã", "Em 5 dias" or "Encerrado". */
export function fmtRelativeDays(iso: string, now = Date.now()): string {
  const startOfDay = (ms: number) => new Date(ms).setHours(0, 0, 0, 0);
  const diffDays = Math.round(
    (startOfDay(new Date(iso).getTime()) - startOfDay(now)) / 86400000
  );
  if (diffDays < 0) return 'Encerrado';
  if (diffDays === 0) return 'Hoje';
  if (diffDays === 1) return 'Amanhã';
  return `Em ${diffDays} dias`;
}

/** Place name shown in lists: name, or the address. */
export function fmtPlace(loc: ChallengeLocation): string {
  return loc.name || loc.address || '';
}

/** "Rua Fonte do Boi, 220 · Salvador, BA" */
export function fmtAddress(loc: ChallengeLocation): string {
  const cityState = [loc.city, loc.state].filter(Boolean).join(', ');
  return [loc.address, cityState].filter(Boolean).join(' · ');
}

/** Full address for maps and clipboard: "Rua X, 1, Salvador - BA" */
export function fullAddress(loc: ChallengeLocation): string {
  const cityState = [loc.city, loc.state].filter(Boolean).join(' - ');
  return [loc.address, cityState].filter(Boolean).join(', ');
}
